import React, { useMemo, useState } from "react";
import { Dropdown } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";

import { Multi, rangoDe } from "../../managment/enrollmentRequests/filtrosMatriculas";
import "../../managment/enrollmentRequests/filtrosMatriculas.css";
import { FilterOptions, KitFilters, ScheduleOption } from "../kits/shared";
import { kitDeliveryStatus } from "../../../../constants";

/*
	Barra de pastillas de Ventas y Entregas (Módulos Claros, 2026-11-01), con las piezas de Matrículas: fecha en segmentos
	(Hoy · Esta Semana · Este Mes · Rango), pastillas que se aplican solas y «Más Filtros» que abre una segunda fila con
	Curso, Periodo, Horario, Maestra y Día. Las opciones de esa fila se cascadean igual que la barra vieja de Tienda.
*/

export type Preset = "" | "hoy" | "semana" | "mes" | "rango";
export interface FiltroTienda {
	preset: Preset; from: string; to: string;
	/** Ventas: "" (todo lo vendido) | pagado | abono | sinPago | anulado. */
	pago: string;
	entrega: string[];
	productos: string[];
	vendidoPor: string[];
	cursos: string[]; periodos: string[]; horarios: string[]; maestras: string[]; dias: string[];
}
export const FILTRO_VACIO: FiltroTienda = { preset: "", from: "", to: "", pago: "", entrega: [], productos: [], vendidoPor: [], cursos: [], periodos: [], horarios: [], maestras: [], dias: [] };

export const PAGO_OPCIONES = [
	{ id: "pagado", name: "Pagado" }, { id: "abono", name: "Con Abono" }, { id: "sinPago", name: "Sin Pago" }, { id: "anulado", name: "Anulado" }
];
export const ENTREGA_OPCIONES = [
	{ id: String(kitDeliveryStatus.PENDING), name: "Sin Entregar" },
	{ id: String(kitDeliveryStatus.PARTIAL), name: "Entrega Parcial" },
	{ id: String(kitDeliveryStatus.UNKNOWN), name: "Sin Registro" },
	{ id: String(kitDeliveryStatus.DELIVERED), name: "Entregado" }
];

/** «Hoy», «Esta Semana» y «Este Mes» se recalculan al pedir: si la pantalla quedó abierta desde ayer, siguen siendo de hoy. */
export const fechasDe = (f: FiltroTienda): { from: string; to: string } => {
	if (f.preset && f.preset !== "rango") {
		const r = rangoDe(f.preset);
		return { from: r.dateFrom, to: r.dateTo };
	}
	return { from: f.from, to: f.to };
};

/** Los filtros de la barra en el formato del backend (KitController::FILTER_KEYS). La fecha va al campo que diga cada pestaña. */
export const aKitFilters = (f: FiltroTienda, campoFecha: "paid" | "lastPaid" | "charged"): KitFilters => {
	const { from, to } = fechasDe(f);
	const fechas: KitFilters = campoFecha === "paid" ? { paidFrom: from, paidTo: to } : campoFecha === "lastPaid" ? { lastPaidFrom: from, lastPaidTo: to } : { from, to };
	return {
		...fechas,
		articleIds: f.productos, advisorIds: f.vendidoPor, deliveryStatusIds: f.entrega,
		courseIds: f.cursos, periodIds: f.periodos, scheduleIds: f.horarios, mentorIds: f.maestras, weekdays: f.dias
	};
};

export const masFiltrosActivos = (f: FiltroTienda): number => [f.cursos, f.periodos, f.horarios, f.maestras, f.dias].filter((l) => l.length > 0).length;

const MESES = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
const corta = (s: string) => { const [y, m, d] = s.split("-").map(Number); return y ? `${d} ${MESES[m - 1]}` : ""; };

/** Una sola opción (Pago): misma pastilla que Matrículas. */
const Una: React.FC<{ titulo: string; opciones: { id: string; name: string }[]; valor: string; onChange: (v: string) => void }> = ({ titulo, opciones, valor, onChange }) => {
	const elegida = opciones.find((o) => o.id === valor);
	return (
		<Dropdown className="waFm">
			<Dropdown.Toggle as="button" type="button" className={"waFmBoton" + (valor ? " activo" : "")}>
				<span className="waFmTitulo">{titulo}</span>
				{elegida ? <span className="waFmValor">{elegida.name}</span> : null}
				<FontAwesomeIcon icon={solid("chevron-down")} className="waFmFlecha" />
			</Dropdown.Toggle>
			<Dropdown.Menu className="waFmMenu" popperConfig={{ strategy: "fixed" }} renderOnMount>
				<div className="waFmLista">
					{opciones.map((o) => (
						<Dropdown.Item as="button" type="button" key={o.id} className={"waFmOpcion" + (o.id === valor ? " elegida" : "")} onClick={() => onChange(o.id === valor ? "" : o.id)}>
							<span className="waFmNombre">{o.name}</span>
							{o.id === valor ? <FontAwesomeIcon icon={solid("check")} style={{ color: "var(--wa-acento)" }} /> : null}
						</Dropdown.Item>
					))}
				</div>
				{valor ? <div className="waFmPie"><button type="button" className="btn btn-link btn-sm" onClick={() => onChange("")}>Quitar</button></div> : null}
			</Dropdown.Menu>
		</Dropdown>
	);
};

export interface FiltrosTiendaProps {
	valor: FiltroTienda;
	onChange: (v: FiltroTienda) => void;
	opciones: FilterOptions | null;
	/** Texto antes de los segmentos de fecha («Vendidas», «Pagadas»). Sin él no hay fechas. */
	etiquetaFecha?: string;
	pago?: boolean;
	entrega?: boolean;
	producto?: boolean;
	vendidoPor?: boolean;
	mas?: boolean;
	/** Lo que la pantalla considera «sin filtros» (Limpiar Filtros vuelve aquí). */
	base?: FiltroTienda;
	/** Algo a la izquierda de la fecha (los segmentos de Margen). */
	antes?: React.ReactNode;
}

const FiltrosTienda: React.FC<FiltrosTiendaProps> = ({ valor, onChange, opciones, etiquetaFecha, pago, entrega, producto, vendidoPor, mas, base, antes }) => {
	const [desde, setDesde] = useState(valor.from);
	const [hasta, setHasta] = useState(valor.to);
	const [abierto, setAbierto] = useState(false);
	const [verMas, setVerMas] = useState<boolean>(masFiltrosActivos(valor) > 0);

	const elegirPreset = (p: Preset) => {
		if (valor.preset === p) { onChange({ ...valor, preset: "", from: "", to: "" }); return; }
		const r = rangoDe(p);
		onChange({ ...valor, preset: p, from: r.dateFrom, to: r.dateTo });
	};
	const aplicarRango = () => {
		const [a, b] = desde && hasta && desde > hasta ? [hasta, desde] : [desde, hasta || desde];
		onChange({ ...valor, preset: a ? "rango" : "", from: a, to: b });
		setAbierto(false);
	};
	const textoRango = valor.preset === "rango" && valor.from ? (valor.from === valor.to ? corta(valor.from) : `${corta(valor.from)} – ${corta(valor.to)}`) : "";
	const presets: { key: Preset; label: string }[] = [{ key: "hoy", label: "Hoy" }, { key: "semana", label: "Esta Semana" }, { key: "mes", label: "Este Mes" }];

	// Curso / Periodo / Horario / Maestra / Día: cada lista enseña solo lo que sigue siendo posible con lo ya elegido.
	const cascada = useMemo(() => {
		const schedules: ScheduleOption[] = opciones?.schedules || [];
		const sel = {
			courses: valor.cursos.flatMap((id) => id.split(",").map(Number)), periods: valor.periodos.map(Number), schedules: valor.horarios.map(Number),
			mentors: valor.maestras.map(Number), weekdays: valor.dias.map(Number)
		};
		const matches = (s: ScheduleOption, skip: keyof typeof sel): boolean =>
			(skip === "courses" || !sel.courses.length || sel.courses.includes(s.courseId))
			&& (skip === "periods" || !sel.periods.length || sel.periods.includes(s.periodId))
			&& (skip === "schedules" || !sel.schedules.length || sel.schedules.includes(s.id))
			&& (skip === "mentors" || !sel.mentors.length || (s.mentorId !== null && sel.mentors.includes(s.mentorId)))
			&& (skip === "weekdays" || !sel.weekdays.length || s.weekdays.some((d) => sel.weekdays.includes(d)));
		const c = new Set<number>(), p = new Set<number>(), m = new Set<number>(), w = new Set<number>(), h = new Set<number>();
		schedules.forEach((s) => {
			if (matches(s, "courses")) c.add(s.courseId);
			if (matches(s, "periods")) p.add(s.periodId);
			if (matches(s, "mentors") && s.mentorId !== null) m.add(s.mentorId);
			if (matches(s, "weekdays")) s.weekdays.forEach((d) => w.add(d));
			if (matches(s, "schedules")) h.add(s.id);
		});
		const any = Object.values(sel).some((l) => l.length > 0);
		return {
			cursos: (opciones?.courses || []).filter((o) => !any || o.id.split(",").map(Number).some((id) => c.has(id))).map((o) => ({ id: o.id, name: o.name })),
			periodos: (opciones?.periods || []).filter((o) => !any || p.has(o.id)).map((o) => ({ id: String(o.id), name: o.name })),
			maestras: (opciones?.mentors || []).filter((o) => !any || m.has(o.id)).map((o) => ({ id: String(o.id), name: o.name })),
			dias: (opciones?.weekdays || []).filter((o) => !any || w.has(o.id)).map((o) => ({ id: String(o.id), name: o.name })),
			horarios: schedules.filter((s) => !any || h.has(s.id)).map((s) => ({ id: String(s.id), name: s.label }))
		};
	}, [opciones, valor.cursos, valor.periodos, valor.horarios, valor.maestras, valor.dias]);

	const normal = (v: any) => Array.isArray(v) ? [...v].sort().join(",") : String(v ?? "");
	const ref = base || FILTRO_VACIO;
	const hay = (Object.keys(FILTRO_VACIO) as (keyof FiltroTienda)[]).some((k) => k !== "from" && k !== "to" && normal(valor[k]) !== normal(ref[k]));
	const nMas = masFiltrosActivos(valor);

	return (
		<>
			<div className="waFmBarra">
				<div className="waFmBarraFecha">
					{antes}
					{etiquetaFecha ? <>
						<span className="waFmBarraEtiqueta">{etiquetaFecha}</span>
						<div className="waFmSegs">
							{presets.map((p) => <button key={p.key} type="button" className={"waFmSeg" + (valor.preset === p.key ? " activo" : "")} onClick={() => elegirPreset(p.key)}>{p.label}</button>)}
							<Dropdown show={abierto} onToggle={(s) => { setAbierto(s); if (s) { setDesde(valor.from); setHasta(valor.to); } }} autoClose="outside">
								<Dropdown.Toggle as="button" type="button" className={"waFmSeg waFmSegRango" + (valor.preset === "rango" ? " activo" : "")}>
									<FontAwesomeIcon icon={solid("calendar-days")} />&nbsp;{textoRango || "Rango"}
								</Dropdown.Toggle>
								<Dropdown.Menu className="waFmMenu waFmRangoMenu" popperConfig={{ strategy: "fixed" }} renderOnMount>
									<div className="waFmRangoCampos">
										<label>Desde<input type="date" value={desde} max={hasta || undefined} onChange={(e) => setDesde(e.target.value)} /></label>
										<label>Hasta<input type="date" value={hasta} min={desde || undefined} onChange={(e) => setHasta(e.target.value)} /></label>
									</div>
									<div className="waFmRangoPie">
										<button type="button" className="btn btn-link btn-sm" onClick={() => { setDesde(""); setHasta(""); }}>Borrar</button>
										<button type="button" className="btn btn-primary btn-sm rounded-pill" disabled={!desde} onClick={aplicarRango}>Aplicar</button>
									</div>
								</Dropdown.Menu>
							</Dropdown>
						</div>
					</> : null}
				</div>
				<div className="waFmBarraListas">
					{pago ? <Una titulo="Pago" opciones={PAGO_OPCIONES} valor={valor.pago} onChange={(v) => onChange({ ...valor, pago: v })} /> : null}
					{entrega ? <Multi titulo="Entrega" opciones={ENTREGA_OPCIONES} elegidos={valor.entrega} onChange={(v) => onChange({ ...valor, entrega: v })} /> : null}
					{producto ? <Multi titulo="Producto" buscar opciones={(opciones?.articles || []).map((a) => ({ id: String(a.id), name: a.name }))}
						elegidos={valor.productos} onChange={(v) => onChange({ ...valor, productos: v })} /> : null}
					{vendidoPor ? <Multi titulo="Vendido Por" buscar opciones={opciones?.advisors || []} elegidos={valor.vendidoPor} onChange={(v) => onChange({ ...valor, vendidoPor: v })} /> : null}
					{mas ? <button type="button" className={"waFmBoton" + (verMas || nMas ? " activo" : "")} onClick={() => setVerMas(!verMas)}>
						<span className="waFmTitulo">Más Filtros</span>{nMas ? <span className="waFmCuenta">{nMas}</span> : null}
						<FontAwesomeIcon icon={verMas ? solid("chevron-up") : solid("chevron-down")} className="waFmFlecha" />
					</button> : null}
					{hay ? <button type="button" className="btn btn-link btn-sm waFmBarraLimpiar" onClick={() => onChange({ ...ref })}>Limpiar Filtros</button> : null}
				</div>
			</div>
			{mas && verMas ? (
				<div className="waFmBarraListas wvtMas">
					<Multi titulo="Curso" buscar opciones={cascada.cursos} elegidos={valor.cursos} onChange={(v) => onChange({ ...valor, cursos: v })} />
					<Multi titulo="Periodo" opciones={cascada.periodos} elegidos={valor.periodos} onChange={(v) => onChange({ ...valor, periodos: v })} />
					<Multi titulo="Horario" buscar opciones={cascada.horarios} elegidos={valor.horarios} onChange={(v) => onChange({ ...valor, horarios: v })} />
					<Multi titulo="Maestra" buscar opciones={cascada.maestras} elegidos={valor.maestras} onChange={(v) => onChange({ ...valor, maestras: v })} />
					<Multi titulo="Día" opciones={cascada.dias} elegidos={valor.dias} onChange={(v) => onChange({ ...valor, dias: v })} />
				</div>
			) : null}
		</>
	);
};

export default FiltrosTienda;
